import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import ProfileAvatar from "../shared/ProfileAvatar";
import {
  clearAdminSearchHistory,
  pushAdminSearchHistory,
  readAdminSearchHistory,
} from "../../utils/adminSearchHistory";
import { showToast } from "../../utils/toast";
import "./OpsAdminSearchModal.css";

function SearchIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="9" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function statusTone(status) {
  const s = String(status || "").toLowerCase();
  if (/approv|active|success|paid|verified|complete/.test(s)) return "green";
  if (/pending|upcoming|expiring/.test(s)) return "amber";
  if (/reject|cancel|suspend|fail/.test(s)) return "red";
  return "blue";
}

const QUICK_ACCESS = [
  {
    id: "add-customer",
    title: "Add Customer",
    description: "Register new customer",
    to: "/admin/customers?create=1",
    tone: "blue",
    icon: "user",
  },
  {
    id: "add-caregiver",
    title: "Add Caregiver",
    description: "Register new caregiver",
    to: "/admin/caregivers?create=1",
    tone: "teal",
    icon: "heart",
  },
  {
    id: "new-booking",
    title: "New Booking",
    description: "Create a new booking",
    to: "/admin/bookings?create=1",
    tone: "purple",
    icon: "calendar",
  },
  {
    id: "verification",
    title: "Verification Queue",
    description: "Pending applications",
    to: "/admin/verification",
    tone: "green",
    icon: "shield",
    countKey: "pendingVerifications",
  },
  {
    id: "complaints",
    title: "Open Complaints",
    description: "Open cases",
    to: "/admin/complaints",
    tone: "red",
    icon: "alert",
    countKey: "openComplaints",
  },
  {
    id: "reports",
    title: "Today's Reports",
    description: "View key reports",
    to: "/admin/reports",
    tone: "orange",
    icon: "chart",
  },
];

function QuickAccessIcon({ name }) {
  if (name === "calendar") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M4 9h16M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "shield") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z" stroke="currentColor" strokeWidth="1.6" />
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "alert") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M10.3 4.5h3.4L20 19H4L10.3 4.5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "chart") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 18V10M12 18V6M18 18v-8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "heart") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 20s-7-4.4-7-10a4 4 0 017-2.2A4 4 0 0119 10c0 5.6-7 10-7 10z"
          stroke="currentColor"
          strokeWidth="1.6"
        />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="9" r="3.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M6 19c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function GroupIcon({ groupId }) {
  if (groupId === "bookings") {
    return (
      <span className="ops-admin-search__row-icon ops-admin-search__row-icon--booking" aria-hidden="true">
        <QuickAccessIcon name="calendar" />
      </span>
    );
  }
  if (groupId === "subscriptions") {
    return (
      <span className="ops-admin-search__row-icon ops-admin-search__row-icon--subscription" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="5" y="4" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
          <path d="M9 9h6M9 13h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  if (groupId === "payments") {
    return (
      <span className="ops-admin-search__row-icon ops-admin-search__row-icon--payment" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.6" />
          <path d="M3 10h18" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      </span>
    );
  }
  return null;
}

export default function OpsAdminSearchModal({ open, onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 280);
  const [recent, setRecent] = useState(readAdminSearchHistory);
  const [loading, setLoading] = useState(false);
  const [popularSearches, setPopularSearches] = useState([]);
  const [quickAccess, setQuickAccess] = useState(null);
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState("");

  const loadResults = useCallback(async (term) => {
    setError("");
    setLoading(true);
    try {
      const res = await api.searchAdminGlobal({ q: term, limit: 8 });
      setPopularSearches(Array.isArray(res?.popularSearches) ? res.popularSearches : []);
      setQuickAccess(res?.quickAccess || null);
      setGroups(Array.isArray(res?.groups) ? res.groups : []);
    } catch (err) {
      setError(err?.message || "Search failed");
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setQuery("");
    setRecent(readAdminSearchHistory());
    loadResults("");
    const timer = window.setTimeout(() => inputRef.current?.focus(), 40);
    function onKeyDown(event) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose, loadResults]);

  useEffect(() => {
    if (!open) return;
    loadResults(debouncedQuery.trim());
  }, [debouncedQuery, open, loadResults]);

  const hasQuery = debouncedQuery.trim().length > 0;
  const primaryViewAllHref = useMemo(() => {
    if (!hasQuery) return "/admin/bookings";
    const q = encodeURIComponent(debouncedQuery.trim());
    if (groups.length) return groups[0].viewAllHref || `/admin/bookings?q=${q}`;
    return `/admin/bookings?q=${q}`;
  }, [debouncedQuery, groups, hasQuery]);

  function closeAndNavigate(to) {
    const trimmed = query.trim();
    if (trimmed) {
      setRecent(pushAdminSearchHistory(trimmed));
    }
    onClose();
    navigate(to);
  }

  function applySearchTerm(term) {
    setQuery(term);
    inputRef.current?.focus();
  }

  function handleSubmit(event) {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    setRecent(pushAdminSearchHistory(trimmed));
    closeAndNavigate(primaryViewAllHref);
  }

  if (!open) return null;

  return (
    <div className="ops-admin-search-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="ops-admin-search"
        role="dialog"
        aria-modal="true"
        aria-label="Admin dashboard search"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <form className="ops-admin-search__bar" onSubmit={handleSubmit}>
          <SearchIcon />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search anything..."
            aria-label="Search anything"
            autoComplete="off"
          />
          <kbd>Ctrl + K</kbd>
          <button type="button" className="ops-admin-search__close" aria-label="Close search" onClick={onClose}>
            ×
          </button>
        </form>

        <div className="ops-admin-search__body">
          {recent.length ? (
            <section className="ops-admin-search__section">
              <div className="ops-admin-search__section-head">
                <h2>Recent Searches</h2>
                <button
                  type="button"
                  className="ops-admin-search__link-btn"
                  onClick={() => {
                    setRecent(clearAdminSearchHistory());
                  }}
                >
                  Clear All
                </button>
              </div>
              <div className="ops-admin-search__chips">
                {recent.map((item) => (
                  <button key={item} type="button" className="ops-admin-search__chip" onClick={() => applySearchTerm(item)}>
                    {item}
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          {!hasQuery && popularSearches.length ? (
            <section className="ops-admin-search__section">
              <div className="ops-admin-search__section-head">
                <h2>Popular Searches</h2>
              </div>
              <div className="ops-admin-search__chips">
                {popularSearches.map((item) => (
                  <button key={item} type="button" className="ops-admin-search__chip" onClick={() => applySearchTerm(item)}>
                    {item}
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          {!hasQuery ? (
            <section className="ops-admin-search__section">
              <div className="ops-admin-search__section-head">
                <h2>Quick Access</h2>
              </div>
              <div className="ops-admin-search__quick-grid">
                {QUICK_ACCESS.map((item) => {
                  const count =
                    item.countKey && quickAccess ? Number(quickAccess[item.countKey]) || 0 : null;
                  const description =
                    count != null && count > 0
                      ? `${count.toLocaleString("en-IN")} ${item.id === "verification" ? "pending applications" : "open cases"}`
                      : item.description;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`ops-admin-search__quick ops-admin-search__quick--${item.tone}`}
                      onClick={() => closeAndNavigate(item.to)}
                    >
                      <span className="ops-admin-search__quick-icon">
                        <QuickAccessIcon name={item.icon} />
                      </span>
                      <span className="ops-admin-search__quick-copy">
                        <strong>{item.title}</strong>
                        <span>{description}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}

          {hasQuery ? (
            <section className="ops-admin-search__section ops-admin-search__section--results">
              <div className="ops-admin-search__section-head">
                <h2>Search Results</h2>
                <button
                  type="button"
                  className="ops-admin-search__link-btn"
                  onClick={() => closeAndNavigate(primaryViewAllHref)}
                >
                  View all results →
                </button>
              </div>
              {error ? <p className="ops-admin-search__error">{error}</p> : null}
              {loading ? <p className="ops-admin-search__loading">Searching…</p> : null}
              {!loading && !groups.length && !error ? (
                <p className="ops-admin-search__empty">No matches for “{debouncedQuery.trim()}”.</p>
              ) : null}
              {groups.map((group) => (
                <div key={group.id} className="ops-admin-search__group">
                  <div className="ops-admin-search__group-head">
                    <h3>
                      {group.label} ({group.total})
                    </h3>
                    <button
                      type="button"
                      className="ops-admin-search__link-btn"
                      onClick={() => closeAndNavigate(group.viewAllHref || primaryViewAllHref)}
                    >
                      View all →
                    </button>
                  </div>
                  <ul className="ops-admin-search__list">
                    {group.items.map((item) => (
                      <li key={`${group.id}-${item.id}`}>
                        <button type="button" className="ops-admin-search__row" onClick={() => closeAndNavigate(item.href)}>
                          {group.id === "caregivers" || group.id === "customers" ? (
                            <ProfileAvatar
                              name={item.title}
                              imageUrl={item.avatarUrl}
                              className="ops-admin-search__avatar"
                            />
                          ) : (
                            <GroupIcon groupId={group.id} />
                          )}
                          <span className="ops-admin-search__row-main">
                            <span className="ops-admin-search__row-title">{item.title}</span>
                            {item.subtitle ? <span className="ops-admin-search__row-sub">{item.subtitle}</span> : null}
                          </span>
                          <span className="ops-admin-search__row-meta">
                            {item.status ? (
                              <span className={`ops-admin-search__badge ops-admin-search__badge--${statusTone(item.status)}`}>
                                {item.status}
                              </span>
                            ) : null}
                            {item.meta ? <span className="ops-admin-search__row-extra">{item.meta}</span> : null}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          ) : null}
        </div>

        <footer className="ops-admin-search__foot">
          <span>Press Enter to view all results</span>
          <button
            type="button"
            className="ops-admin-search__link-btn"
            onClick={() =>
              showToast.info("Search by name, ID, phone, booking number, NCID, subscription or payment reference.")
            }
          >
            Search tips
          </button>
        </footer>
      </div>
    </div>
  );
}
